/**
 * DriveStatusCard — iter189b Drive Sync widget.
 * Shows pending + failed Drive uploads and offers a one-tap retry.
 * Admin / manager / account_manager only.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Cloud, CloudOff, RefreshCw, CircleAlert } from "lucide-react";
import DriveUploadProgress from "@/components/DriveUploadProgress";

export default function DriveStatusCard() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [jobId, setJobId] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/admin/files/drive-status");
      setData(r.data);
    } catch {
      setData({ error: true });
    }
  };

  useEffect(() => { load(); }, []);

  const retry = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/files/retry-failed-drive-uploads", {});
      toast.success(`Queued ${r.data?.queued ?? 0} file${r.data?.queued === 1 ? "" : "s"} for retry`);
      if (r.data?.job_id) setJobId(r.data.job_id);
      setTimeout(load, 1500);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Retry failed");
    } finally {
      setBusy(false);
    }
  };

  if (!data) {
    return (
      <Card className="p-5 border-border shadow-none flex items-center gap-3" data-testid="drive-status-card-loading">
        <Cloud size={20} strokeWidth={1.5} className="text-muted-foreground" />
        <span className="text-sm text-muted-foreground">Checking Drive sync…</span>
      </Card>
    );
  }
  if (data.error) return null;

  const pending = data.pending ?? 0;
  const failed = data.failed ?? 0;
  const onDrive = data.on_drive ?? 0;
  const stuckVideos = (data.stuck_videos || []).length;
  const reauthNeeded = !!data.reauth_needed;
  const hasIssues = pending > 0 || failed > 0;
  const accent = failed > 0 ? "text-[#EF4444]" : (pending > 0 ? "text-[#F59E0B]" : "text-[#10B981]");
  const Icon = failed > 0 ? CloudOff : Cloud;

  return (
    <Card className="p-5 border-border shadow-none flex flex-col gap-3" data-testid="drive-status-card">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon size={18} strokeWidth={1.5} className={accent} />
          <span className="text-[11px] font-bold uppercase tracking-[0.15em] text-muted-foreground">
            Google Drive Sync
          </span>
        </div>
        {hasIssues && (
          <Button size="sm" variant="outline" onClick={retry} disabled={busy}
                   data-testid="drive-status-retry"
                   className="h-7 text-[11px]">
            {busy ? <RefreshCw size={12} className="animate-spin mr-1" /> : <RefreshCw size={12} className="mr-1" />}
            Retry
          </Button>
        )}
      </div>
      <div className="flex items-end gap-6 flex-wrap">
        <div>
          <div className="text-3xl md:text-4xl font-light tracking-tighter font-[Outfit]" data-testid="drive-on-count">
            {onDrive}
          </div>
          <div className="text-xs text-muted-foreground">on Drive</div>
        </div>
        {pending > 0 && (
          <div>
            <div className="text-2xl font-light tracking-tighter font-[Outfit] text-[#F59E0B]" data-testid="drive-pending-count">
              {pending}
            </div>
            <div className="text-xs text-muted-foreground">pending</div>
          </div>
        )}
        {failed > 0 && (
          <div>
            <div className="text-2xl font-light tracking-tighter font-[Outfit] text-[#EF4444]" data-testid="drive-failed-count">
              {failed}
            </div>
            <div className="text-xs text-muted-foreground">failed</div>
          </div>
        )}
      </div>
      {stuckVideos > 0 && !reauthNeeded && (
        <div className="flex items-center gap-2 text-xs text-[#EF4444]" data-testid="drive-stuck-videos">
          <CircleAlert size={12} />
          <span>{stuckVideos} stuck video{stuckVideos === 1 ? "" : "s"} — retry to push them</span>
        </div>
      )}
      {reauthNeeded && (
        <div className="flex items-center justify-between gap-2 text-xs bg-[#FEF3C7] border border-[#F59E0B]/40 rounded-md px-3 py-2" data-testid="drive-reauth-needed">
          <div className="flex items-center gap-2 text-[#92400E]">
            <CircleAlert size={13} />
            <span>Google Drive token expired — uploads will fail until you reconnect.</span>
          </div>
          <a href="/settings" className="underline font-semibold text-[#92400E] whitespace-nowrap"
             data-testid="drive-reauth-link">
            Reconnect →
          </a>
        </div>
      )}
      {!hasIssues && (
        <div className="text-xs text-muted-foreground">All approved files are safely on Drive.</div>
      )}
      {jobId && (
        <DriveUploadProgress jobId={jobId} onClose={() => { setJobId(null); load(); }} />
      )}
    </Card>
  );
}
